import { Injectable, inject } from '@angular/core';
import { CmsApiService } from './api/cms-api.service';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, shareReplay, tap, switchMap } from 'rxjs/operators';
import { RedirectorService } from './redirector.service';
import { PlatformService } from './platform.service';
import { isBot } from 'ngx-unificator/helpers';
import { DeviceDetectorService } from 'ngx-unificator/services';
import { Router } from '@angular/router';

@Injectable({
  providedIn: 'root',
})
export class EnvironmentService {
  private _api = inject(CmsApiService);
  private _redirector = inject(RedirectorService);
  private _platform = inject(PlatformService);
  private _device = inject(DeviceDetectorService);
  private _router = inject(Router);


  /**
   * Environment observable
   */
  private _environment$: Observable<any> = this._api.serviceEnvironment().pipe(
    switchMap(e => !e ? throwError({ error: { error: { stack: 'SyntaxError' } } }) : of(e)),
    catchError(error => {
      this._redirector.checkError(error);
      return of({
        data: {
          cacheControl: [],
          languageList: [],
          countryList: [],
          currency: {
            enabled: true,
            short: 'AUD',
            symbol: 'AU$',
            symbolNative: '$',
          },
          country: {
            isRestricted: false,
            short: 'au',
          },
          locale: {
            short: 'en',
          },
        },
      });
    }),
    map((response: any) => {
      response.data.languageList.map(lang => {
        lang.short = this._checkShortLocale(lang.short);
      });

      response.data.locale.short = this._checkShortLocale(response.data.locale.short);

      return {
        ...response,
      };

    }),
    shareReplay(1),
    tap(response => {
      this._environment = response.data;
      this._setIpCountryHeaderApi();
      this._checkRestrictCountry();
    }),
  );

  /**
   * Environment object
   */
  private _environment;

  /**
   * Returns environment ReplaySubject
   */
  get env$(): Observable<any> {
    return this._environment$;
  }

  /**
   * Returns environment object
   */
  get env(): any {
    return this._environment;
  }

  private _checkRestrictCountry() {
    if (this._platform.isBrowser &&
      !isBot(this._device.userAgent) &&
      this._environment.country.isRestricted) {
      setTimeout(() => {
        this._router.navigateByUrl('/403');
      }, 1000);
    }
  }


  /**
   * Is user from Sweden
   */
  get isSe(): boolean {
    return this._environment?.country?.short === 'se';
  }

  /**
   * Check and change short locale
   * @param locale
   * @private
   */
  private _checkShortLocale(locale: string) {
    switch (locale) {
      case 'ce':
        return 'en-ca';
      case 'nz':
        return 'en-nz';
      case 'au':
        return 'en-au';
      default:
        return locale;
    }
  }

  /**
   * Returns true if user ip from provided countries
   *
   * @param countries
   */
  public isUserFrom(countries: string | string[]): boolean {
    if (!Array.isArray(countries)) {
      countries = [countries];
    }

    return this._environment
      && this._environment.country
      && countries.includes(this._environment.country.short);
  }

  /**
   * Set IP-Country header to CMS API
   * @private
   */
  private _setIpCountryHeaderApi() {
    this._api.customHeadersList.push({ key: 'IP-Country', val: this._environment.country.short });
  }
}
